/**
 * `cut` — seax, axe, sword, sickle, broken quern-stone.
 *
 * The commonest damage type and the easiest to treat: wide wounds, visible
 * bleeding, a wound you can see the whole of and therefore bind. It severs
 * where blunt breaks and puncture perforates.
 */
import { fxAt4and5, ladder } from './authoring.js';
const CUT = [
    { bleed: 0, shock: 0, pain: 0, impair: 0, knock: 0, infect: 0 },
    { bleed: 1, shock: 1, pain: 1, impair: 0.05, knock: 0, infect: 0.02 },
    { bleed: 3, shock: 2, pain: 2, impair: 0.15, knock: 0, infect: 0.05 },
    { bleed: 6, shock: 4, pain: 4, impair: 0.35, knock: -1, infect: 0.1 },
    { bleed: 10, shock: 6, pain: 6, impair: 0.65, knock: -3, infect: 0.15 },
    { bleed: 16, shock: 9, pain: 9, impair: 1, knock: -5, infect: 0.2 },
];
export const CUT_TABLE = {
    head: ladder(CUT, {
        bleed: 1.2,
        shock: 1.8,
        pain: 1.2,
        knock: -2,
        fx: [[], [], [], ['knockdown'], ['break', 'knockdown'], ['kill']],
    }, [
        [
            '{weapon} glances off {victim}’s scalp and raises a welt.',
            '{weapon} skims {victim}’s brow and draws a thin line of blood.',
        ],
        [
            '{weapon} opens the scalp above {victim}’s ear and blood sheets down {their} face.',
            '{weapon} lays {victim}’s cheek open.',
        ],
        [
            '{weapon} cuts deep into {victim}’s scalp and grates on the bone beneath.',
            '{weapon} splits {victim}’s brow to the bone and {their} eye fills with blood.',
        ],
        [
            '{weapon} bites into {victim}’s skull and hangs there a moment.',
            '{weapon} shears a flap of scalp from {victim}’s head and chips the bone.',
        ],
        [
            '{weapon} caves in the side of {victim}’s skull.',
            '{weapon} splits {victim}’s jaw and drives the bone inward.',
        ],
        [
            '{weapon} cleaves {victim}’s skull to the teeth.',
            '{weapon} takes {victim}’s head half from {their} shoulders.',
        ],
    ]),
    eye: ladder(CUT, {
        bleed: 0.4,
        shock: 1.5,
        pain: 1.3,
        fx: [[], [], [], ['blind'], ['blind'], ['blind', 'break']],
    }, [
        [
            '{weapon} passes close enough to sting {victim}’s eye.',
            '{weapon} nicks the brow above {victim}’s {part}.',
        ],
        [
            '{weapon} slices the lid of {victim}’s {part}.',
            '{weapon} opens the flesh beside {victim}’s {part} and blood runs into it.',
        ],
        [
            '{weapon} lays open the socket around {victim}’s {part}.',
            '{weapon} cuts across {victim}’s {part} and {victim} claws at {their} face.',
        ],
        [
            '{weapon} opens {victim}’s {part}. It is ruined.',
            '{weapon} takes the sight from {victim}’s {part}.',
        ],
        [
            '{weapon} cuts {victim}’s {part} clean from its socket.',
            '{weapon} empties the socket of {victim}’s {part}.',
        ],
        [
            '{weapon} drives through {victim}’s {part} and into the skull behind it.',
            '{weapon} tears away {victim}’s {part} and the bone around it.',
        ],
    ]),
    neck: ladder(CUT, { bleed: 3, shock: 1.5, pain: 1.2, knock: -1, fx: [[], [], [], [], [], ['kill']] }, [
        [
            '{weapon} scrapes the side of {victim}’s neck.',
            '{weapon} raises a red line across {victim}’s throat.',
        ],
        [
            '{weapon} opens a shallow cut across {victim}’s neck.',
            '{weapon} slices the skin of {victim}’s throat and blood wells up.',
        ],
        [
            '{weapon} cuts into the muscle of {victim}’s neck and the blood comes fast.',
            '{weapon} opens {victim}’s neck and {victim} claps a hand to it.',
        ],
        [
            '{weapon} cuts deep into {victim}’s throat. Blood comes in gouts.',
            '{weapon} opens the great vessels of {victim}’s neck.',
        ],
        [
            '{weapon} opens {victim}’s throat to the windpipe and the breath whistles out of it.',
            '{weapon} lays {victim}’s neck open to the spine.',
        ],
        [
            '{weapon} cuts {victim}’s throat through.',
            '{weapon} takes {victim}’s head from {their} shoulders.',
        ],
    ]),
    chest: ladder(CUT, { bleed: 1.5, shock: 1.3, fx: [[], [], [], [], ['break'], ['kill']] }, [
        [
            '{weapon} scrapes across {victim}’s ribs.',
            '{weapon} draws a shallow line across {victim}’s chest.',
        ],
        [
            '{weapon} opens a gash across {victim}’s chest.',
            '{weapon} cuts the skin over {victim}’s ribs.',
        ],
        [
            '{weapon} cuts through the muscle of {victim}’s chest and grates on rib.',
            '{weapon} lays {victim}’s chest open in a long red line.',
        ],
        [
            '{weapon} bites into {victim}’s ribs and sticks.',
            '{weapon} cuts deep across {victim}’s chest and the bone shows white.',
        ],
        [
            '{weapon} shears through {victim}’s ribs and into what lies behind them.',
            '{weapon} opens {victim}’s chest and {their} breath comes in a wet rattle.',
        ],
        [
            '{weapon} splits {victim}’s chest open.',
            '{weapon} cleaves through {victim}’s ribs to the heart.',
        ],
    ]),
    abdomen: ladder(CUT, { bleed: 1.3, infect: 2.5, fx: [[], [], [], [], [], ['kill']] }, [
        ['{weapon} scrapes across {victim}’s belly.', '{weapon} raises a welt along {victim}’s side.'],
        [
            '{weapon} opens the skin of {victim}’s belly.',
            '{weapon} cuts a shallow line across {victim}’s flank.',
        ],
        [
            '{weapon} cuts into the muscle of {victim}’s belly.',
            '{weapon} opens {victim}’s side and {victim} folds around the wound.',
        ],
        [
            '{weapon} cuts through the wall of {victim}’s belly.',
            '{weapon} opens {victim}’s gut and the smell of it carries.',
        ],
        [
            '{weapon} lays {victim}’s belly open and the gut spills against {their} hands.',
            '{weapon} opens {victim}’s side from rib to hip.',
        ],
        [
            '{weapon} guts {victim} where {victim} stands.',
            '{weapon} opens {victim}’s belly to the spine.',
        ],
    ]),
    arm: ladder(CUT, { fx: fxAt4and5(['break'], ['sever']) }, [
        ['{weapon} skitters off {victim}’s {part}.', '{weapon} raises a welt on {victim}’s {part}.'],
        [
            '{weapon} opens a gash along {victim}’s {part}.',
            '{weapon} cuts the skin of {victim}’s {part}.',
        ],
        [
            '{weapon} cuts into the muscle of {victim}’s {part}.',
            '{weapon} lays open {victim}’s {part} and the blood runs to {their} elbow.',
        ],
        [
            '{weapon} cuts deep into {victim}’s {part} and grates on the bone.',
            '{weapon} opens {victim}’s {part} to the bone and the arm goes slack.',
        ],
        [
            '{weapon} shatters the bone of {victim}’s {part}.',
            '{weapon} cuts through {victim}’s {part} and it hangs by muscle alone.',
        ],
        ['{weapon} takes {victim}’s {part} off at the shoulder.', '{weapon} severs {victim}’s {part}.'],
    ]),
    hand: ladder(CUT, { bleed: 0.7, impair: 1.1, fx: fxAt4and5(['break'], ['sever']) }, [
        [
            '{weapon} raps {victim}’s {part} and leaves a red mark.',
            '{weapon} scrapes the back of {victim}’s {part}.',
        ],
        ['{weapon} opens the skin across {victim}’s {part}.', '{weapon} slices {victim}’s knuckles.'],
        [
            '{weapon} cuts into {victim}’s {part} and the fingers will not close.',
            '{weapon} opens {victim}’s {part} across the palm.',
        ],
        [
            '{weapon} cuts the tendons of {victim}’s {part}.',
            '{weapon} takes two fingers from {victim}’s {part}.',
        ],
        [
            '{weapon} shatters the bones of {victim}’s {part}.',
            '{weapon} cuts through {victim}’s {part} and leaves it a ruin.',
        ],
        ['{weapon} takes {victim}’s {part} off at the wrist.', '{weapon} severs {victim}’s {part}.'],
    ]),
    leg: ladder(CUT, { bleed: 1.1, knock: -2, fx: fxAt4and5(['break', 'knockdown'], ['sever', 'knockdown']) }, [
        [
            '{weapon} skitters off {victim}’s {part}.',
            '{weapon} raises a welt along {victim}’s {part}.',
        ],
        [
            '{weapon} opens a gash along {victim}’s {part}.',
            '{weapon} cuts the skin of {victim}’s {part}.',
        ],
        [
            '{weapon} cuts into the muscle of {victim}’s {part} and {victim} staggers.',
            '{weapon} lays {victim}’s {part} open along the thigh.',
        ],
        [
            '{weapon} cuts deep into {victim}’s {part} and grates on the bone.',
            '{weapon} opens the back of {victim}’s {part} and the leg gives under {them}.',
        ],
        [
            '{weapon} shatters the bone of {victim}’s {part}.',
            '{weapon} cuts through {victim}’s {part} at the knee and {victim} goes down.',
        ],
        [
            '{weapon} takes {victim}’s {part} off at the thigh.',
            '{weapon} severs {victim}’s {part} and {victim} falls.',
        ],
    ]),
    foot: ladder(CUT, { bleed: 0.7, knock: -1, fx: fxAt4and5(['break'], ['sever']) }, [
        [
            '{weapon} scrapes across {victim}’s {part}.',
            '{weapon} raps {victim}’s {part} and leaves a red mark.',
        ],
        [
            '{weapon} opens the skin across {victim}’s {part}.',
            '{weapon} cuts into {victim}’s {part} above the ankle.',
        ],
        [
            '{weapon} cuts into {victim}’s {part} and {victim} favours it.',
            '{weapon} opens {victim}’s {part} and the blood pools in {their} shoe.',
        ],
        [
            '{weapon} cuts the tendon at the back of {victim}’s {part}.',
            '{weapon} takes two toes from {victim}’s {part}.',
        ],
        [
            '{weapon} shatters the bones of {victim}’s {part}.',
            '{weapon} cuts through {victim}’s {part} and leaves it a ruin.',
        ],
        ['{weapon} takes {victim}’s {part} off at the ankle.', '{weapon} severs {victim}’s {part}.'],
    ]),
};
//# sourceMappingURL=cut.js.map